import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { MakeCopy, Overflow, Delete, HyperLink } from 'ui/icons';
import { ActionMenu } from 'ui/actionMenu';
import { AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { colors } from 'ui/theme';
import { ShareViewModal } from 'modules/messagingInsights/components/SavedViews/modals/ShareViewModal';
import { DeleteViewModal } from 'modules/messagingInsights/components/SavedViews/modals/DeleteViewModal';
import { deleteSavedViewsRequest } from 'modules/messagingInsights/actions';
import { ListedView } from 'modules/savedViewsIndex/types';
import { CopyModal } from 'modules/messagingInsights/components/SavedViews/modals/CopyModal';
import { useQueryContext } from 'modules/savedViewsIndex/context';
import { selectEmail } from 'core/userContext';

type Props = {
  view: ListedView;
};

export const ViewOverflowMenu: FC<React.PropsWithChildren<Props>> = ({ view }) => {
  const dispatch = useDispatch();
  const userEmail = useSelector(selectEmail);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const { setCurrentPage, toggleRefetchViews } = useQueryContext();
  const shouldShowDeleteOption = view?.createdByEmail === userEmail;

  const IDS = {
    makeACopy: 'make-a-copy',
    share: 'share',
    delete: 'delete',
  };

  const menuOptions = [
    {
      id: IDS.makeACopy,
      content: (
        <>
          <MakeCopy color={colors.textSecondary} height={16} width={16} />
          <span>Make a copy</span>
        </>
      ),
      onClick: () => setShowCopyModal(true),
      as: AllowedActionMenuElements.Button,
    },
    {
      id: IDS.share,
      content: (
        <>
          <HyperLink color={colors.textSecondary} height={16} width={16} />
          <span>Share</span>
        </>
      ),
      onClick: () => setShowShareModal(true),
      as: AllowedActionMenuElements.Button,
    },
    ...(shouldShowDeleteOption
      ? [
          {
            id: IDS.delete,
            content: (
              <>
                <Delete color={colors.textSecondary} height={16} width={16} />
                <span>Delete</span>
              </>
            ),
            onClick: () => setShowDeleteModal(true),
            as: AllowedActionMenuElements.Button,
          },
        ]
      : []),
  ];

  const refetchViewsCallback = () => {
    // fetch the views again after a delete or copy
    setCurrentPage(1);
    toggleRefetchViews();
  };

  const deleteView = () => {
    dispatch(deleteSavedViewsRequest(view, refetchViewsCallback));
  };

  return (
    <>
      <ActionMenu
        align={MENU_ALIGNMENTS.RIGHT}
        options={menuOptions}
        data-test="view-overflow-menu"
      >
        <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
          <Overflow color={colors.grey90} />
        </IconButton>
      </ActionMenu>
      {showCopyModal && (
        <CopyModal
          view={view}
          copyCallback={refetchViewsCallback}
          onClose={() => setShowCopyModal(false)}
        />
      )}
      {showShareModal && <ShareViewModal view={view} onClose={() => setShowShareModal(false)} />}
      {showDeleteModal && (
        <DeleteViewModal
          viewsToBeDeleted={[view]}
          deleteViews={deleteView}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};
